import { CANCEL_GUEST_SCHEDULED, LOAD } from 'model/guestLoanSearch';
import {
  loadByLoanNumber,
  loadByEmail,
  cancelGuestScheduledPayment,
} from 'sdk/guestLoanSearch';
import { takeLatest, call, put, takeEvery } from 'redux-saga/effects';
import { LOAD_HISTORY, LOAD_SCHEDULED } from '../model/guestLoanSearch';
import {
  loadHistoryTransactions,
  loadScheduledTransactions,
} from '../sdk/guestLoanSearch';
import { ADD_MESSAGE } from '../util/actions';

const getGuestLoadError = (error, fallback) => {
  const message = error?.message || '';
  if (/auth(\s+or)?\s+auth\.id|authentication/i.test(message)) {
    return 'Guest authentication is still loading. Please retry when authentication is restored.';
  }
  return message || fallback;
};

const TRANSACTION_PAGE_SIZE = 25;
const MAX_TRANSACTION_OFFSET = 1000;
const DEFAULT_TRANSACTION_ORDER = 'sendon:desc';

const omitGuestDateBounds = (data = {}) => {
  const nextData = { ...data };
  delete nextData.startWithSendOn;
  delete nextData.endWithSendOn;
  return nextData;
};

const normalizeTransactionOrder = (order) => {
  if (typeof order !== 'string') return DEFAULT_TRANSACTION_ORDER;
  const match = /^sendon:(asc|desc)$/i.exec(order.trim());
  return match ? `sendon:${match[1].toLowerCase()}` : DEFAULT_TRANSACTION_ORDER;
};

const getTransactionRequest = (data = {}) => {
  const requested = data.request || data;
  return {
    offset: 0,
    limit: TRANSACTION_PAGE_SIZE,
    order: normalizeTransactionOrder(requested.order),
  };
};

const normalizeTransactionResponse = (response) => {
  const payload = response?.payload;
  const transactions =
    [
      payload?.items,
      payload?.transactions,
      payload?.transactions?.items,
      payload?.data?.items,
      response?.items,
      response?.transactions,
      response?.data?.items,
      response,
    ].find(Array.isArray) || [];
  const totalRecords =
    Number(
      payload?.totalRecords ?? response?.totalRecords ?? transactions.length
    ) || transactions.length;

  return { transactions, totalRecords };
};

function* loadAllTransactionPages(loader, apiData, auth, request) {
  const transactions = [];
  let offset = 0;
  let totalRecords = 0;

  while (offset <= MAX_TRANSACTION_OFFSET) {
    const response = yield call(
      loader,
      {
        ...apiData,
        offset,
        limit: TRANSACTION_PAGE_SIZE,
        order: request.order,
      },
      auth
    );
    const page = normalizeTransactionResponse(response);
    transactions.push(...page.transactions);
    totalRecords = Math.max(totalRecords, page.totalRecords);

    if (page.transactions.length === 0 || transactions.length >= totalRecords) {
      break;
    }

    const nextOffset = offset + page.transactions.length;
    if (nextOffset <= offset || nextOffset > MAX_TRANSACTION_OFFSET) {
      break;
    }
    offset = nextOffset;
  }

  return { transactions, totalRecords };
}

const HISTORY_TRANSACTION_HANDLER = {
  loader: loadHistoryTransactions,
  successType: LOAD_HISTORY.SUCCESS,
  failureType: LOAD_HISTORY.FAILURE,
  fallbackError: 'Unable to load transaction history.',
};

const SCHEDULED_TRANSACTION_HANDLER = {
  loader: loadScheduledTransactions,
  successType: LOAD_SCHEDULED.SUCCESS,
  failureType: LOAD_SCHEDULED.FAILURE,
  fallbackError: 'Unable to load scheduled transactions.',
};

const getTransactionContext = ({ data, auth: actionAuth }) => {
  const {
    auth: dataAuth,
    request: requestedTransactionState,
    ...dataWithoutAuth
  } = data;
  const rest = omitGuestDateBounds(dataWithoutAuth);
  const auth = actionAuth || dataAuth;
  const request = getTransactionRequest({
    ...rest,
    request: requestedTransactionState,
  });
  const apiData = {
    fspId: rest.fspId,
    loanNumber: rest.loanNumber,
  };
  return { rest, auth, request, apiData };
};

function* handleLoadTransactions(action, handler) {
  const { rest, auth, request, apiData } = getTransactionContext(action);
  try {
    const { transactions, totalRecords } = yield* loadAllTransactionPages(
      handler.loader,
      apiData,
      auth,
      request
    );
    yield put({
      type: handler.successType,
      data: {
        loanNumber: rest.loanNumber,
        transactions,
        totalRecords,
        request,
      },
    });
  } catch (error) {
    const message = getGuestLoadError(error, handler.fallbackError);
    yield put({
      type: handler.failureType,
      error: message,
      data: {
        ...rest,
        request,
        error: message,
      },
    });
  }
}

export function* handleLoadHistory(action) {
  yield* handleLoadTransactions(action, HISTORY_TRANSACTION_HANDLER);
}

export function* handleLoadScheduled(action) {
  yield* handleLoadTransactions(action, SCHEDULED_TRANSACTION_HANDLER);
}

export function* handleCancelScheduled({ data }) {
  const { userId, auth, request, ...rest } = data;
  try {
    yield call(cancelGuestScheduledPayment, rest, userId);

    yield put({
      type: CANCEL_GUEST_SCHEDULED.SUCCESS,
      data: {
        loanNumber: rest.loanNumber,
        scheduleId: rest.loanSchedId,
      },
    });

    yield put({
      type: ADD_MESSAGE.ACTION,
      data: 'Payment successfully canceled',
    });

    // Reload scheduled transactions to get updated list
    yield put({
      type: LOAD_SCHEDULED.ACTION,
      data: {
        fspId: rest.fspId,
        loanNumber: rest.loanNumber,
        auth,
        ...(request ? { request, ...request } : {}),
      },
    });
  } catch (error) {
    yield put({
      type: CANCEL_GUEST_SCHEDULED.FAILURE,
      error: error.message,
      data: rest,
    });

    yield put({
      type: ADD_MESSAGE.ACTION,
      data: error.message || 'Failed to cancel payment',
    });
  }
}

export function* loadGuestLoanSearchSaga(action) {
  const { data = {}, auth = {} } = action;
  const { loanNumber, email, fspId, limit, offset } = data;
  try {
    let response;
    if (loanNumber) {
      response = yield call(loadByLoanNumber, { fspId, loanNumber }, auth);
    } else if (email) {
      response = yield call(loadByEmail, { fspId, email, limit, offset }, auth);
    } else {
      throw new Error('Either loanNumber or email must be provided');
    }
    yield put({ type: LOAD.SUCCESS, data: { response } });
  } catch (error) {
    yield put({
      type: LOAD.FAILURE,
      data: {
        error: error?.message || 'Unable to load guest profile.',
        loanNumber,
      },
    });
  }
}

export default function* guestLoanSearchSaga() {
  yield takeEvery(LOAD.ACTION, loadGuestLoanSearchSaga);
  yield takeLatest(LOAD_HISTORY.ACTION, handleLoadHistory);
  yield takeLatest(LOAD_SCHEDULED.ACTION, handleLoadScheduled);
  yield takeEvery(CANCEL_GUEST_SCHEDULED.ACTION, handleCancelScheduled);
}
